import React from 'react';
import { Box, SimpleGrid, Text, VStack, Flex } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader/PageHeader';

// Component card for displaying in the style guide grid
interface ComponentCardProps {
  title: string;
  path: string;
  icon: React.ReactNode;
}

// Style Guide Icons - Embedded SVG components
const DownloadIcon = ({ size = 40, color = '#2B4956' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 25V30C10 31.1046 10.8954 32 12 32H28C29.1046 32 30 31.1046 30 30V25"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M20 8V24"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M14 20L20 26L26 20"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FilterIcon = ({ size = 40, color = '#2B4956' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 10H34"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M10 20H30"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M14 30H26"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const PaginationIcon = ({ size = 40, color = '#2B4956' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="6"
      y="16"
      width="6"
      height="8"
      rx="2"
      stroke={color}
      strokeWidth="2"
    />
    <rect
      x="17"
      y="16"
      width="6"
      height="8"
      rx="2"
      fill={color}
    />
    <rect
      x="28"
      y="16"
      width="6"
      height="8"
      rx="2"
      stroke={color}
      strokeWidth="2"
    />
    <path
      d="M2 20H4"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M36 20H38"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ButtonChoiceIcon = ({ size = 40, color = '#2B4956' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="14"
      width="10"
      height="12"
      rx="6"
      stroke={color}
      strokeWidth="2"
      fill="transparent"
    />
    <rect
      x="16"
      y="14"
      width="10"
      height="12"
      rx="6"
      stroke={color}
      strokeWidth="2"
      fill={color}
    />
    <rect
      x="28"
      y="14"
      width="8"
      height="12"
      rx="6"
      stroke={color}
      strokeWidth="2"
      fill="transparent"
    />
  </svg>
);

const ComponentCard: React.FC<ComponentCardProps> = ({ title, path, icon }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/style-guide/${path}`);
  };

  return (
    <Box
      p={6}
      borderRadius="20px"
      bg="white"
      boxShadow="md"
      transition="all 0.2s"
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: 'md',
        cursor: 'pointer'
      }}
      onClick={handleCardClick}
      height="100%"
    >
      <VStack spacing={4} align="center">
        <Box
          p={4}
          bg="#fff"
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="80px"
          height="80px"
        >
          {icon}
        </Box>
        <Text fontSize="16px" textAlign="center">{title}</Text>
      </VStack>
    </Box>
  );
};

const StyleGuidePage: React.FC = () => {
  const components = [
    {
      title: 'Download Button',
      path: 'download-button',
      icon: <DownloadIcon />,
    },
    {
      title: 'List Filter',
      path: 'list-filter',
      icon: <FilterIcon />,
    },
    {
      title: 'Pagination',
      path: 'pagination',
      icon: <PaginationIcon />,
    },
    {
      title: 'Button Choice',
      path: 'button-choice',
      icon: <ButtonChoiceIcon />,
    },
  ];

  return (
    <Flex flexFlow="column" borderRadius="20px">
      <PageHeader title={"Alea Style Guide"} />
      <Box p={8} bg="#fff" minH="100vh" borderRadius="20px">
        <Box mx="auto">
          <Text mb={8} color="gray.600">A comprehensive guide to our design system and UI components</Text>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {components.map((component, index) => (
              <ComponentCard
                key={index}
                title={component.title}
                path={component.path}
                icon={component.icon}
              />
            ))}
          </SimpleGrid>
        </Box>
      </Box>
    </Flex>
  );
};

export default StyleGuidePage; 